import React, { useEffect, useState, useCallback } from "react";
import { Dialog, DialogContent } from "@material-ui/core";
import { toast } from "react-toastify";
import * as APPCONFIG from "../../../config/app-config";
import MyYuukke from "../../../api/MyYuukke";
import { useSelector } from "react-redux";
import { OPENROUTER_CHAT_URL } from "../../../utils/getOpenRouterChatUrl";

/* ─── inject responsive styles once ─────────────────────────────────────── */
const ADVERT_CSS = `
.adv-dialog .MuiDialog-paper {
  border-radius: 20px !important;
  overflow: hidden !important;
  font-family: 'DM Sans', sans-serif !important;
  max-height: 95vh !important;
}
.adv-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-height: 480px;
}
.adv-left  { padding: 24px; border-right: 1px solid rgba(0,0,0,0.08); background: #fff; overflow-y: auto; }
.adv-right { padding: 24px; background: #f7f7f5; overflow-y: auto; }

/* Platform buttons */
.adv-platform-row { display: flex; gap: 8px; }
.adv-platform-btn { flex: 1; padding: 9px 6px; border-radius: 10px; cursor: pointer; font-weight: 600; font-size: 12px; font-family: inherit; display: flex; flex-direction: column; align-items: center; gap: 4px; transition: all 0.15s; }

/* Tone chips */
.adv-tone-row { display: flex; flex-wrap: wrap; gap: 7px; }

/* Textarea */
.adv-textarea { width: 100%; padding: 12px 14px; font-size: 14px; line-height: 1.65; font-family: inherit; color: #1a1a18; border-radius: 10px; outline: none; resize: vertical; box-sizing: border-box; background: #fafafa; }

/* Buttons */
.adv-btn { padding: 11px 20px; border-radius: 10px; border: none; font-size: 14px; font-weight: 600; cursor: pointer; font-family: inherit; display: flex; align-items: center; justify-content: center; gap: 7px; transition: opacity 0.15s; width: 100%; }

/* Preview card */
.adv-preview-card { background: #fff; border-radius: 14px; border: 1px solid rgba(0,0,0,0.08); overflow: hidden; box-shadow: 0 2px 12px rgba(0,0,0,0.05); }

/* ── TABLET  ≤ 900px ── */
@media (max-width: 900px) {
  .adv-grid { grid-template-columns: 1fr; min-height: unset; }
  .adv-left  { border-right: none; border-bottom: 1px solid rgba(0,0,0,0.08); padding: 20px; }
  .adv-right { padding: 20px; }
  .adv-dialog .MuiDialog-paper { max-height: 90vh !important; }
}

/* ── MOBILE  ≤ 600px ── */
@media (max-width: 600px) {
  .adv-left  { padding: 16px; }
  .adv-right { padding: 16px; }
  .adv-platform-btn { font-size: 11px; padding: 8px 4px; }
  .adv-tone-row  { gap: 5px; }
  .adv-textarea  { font-size: 13px; padding: 10px 12px; }
  .adv-btn       { font-size: 13px; padding: 10px 16px; }
  .adv-dialog .MuiDialog-paper { border-radius: 14px !important; margin: 8px !important; max-height: 92vh !important; }
}

/* ── SMALL MOBILE  ≤ 400px ── */
@media (max-width: 400px) {
  .adv-left  { padding: 13px; }
  .adv-right { padding: 13px; }
  .adv-platform-btn { font-size: 10px; }
  .adv-btn   { font-size: 12px; padding: 9px 12px; }
  .adv-dialog .MuiDialog-paper { margin: 4px !important; border-radius: 12px !important; }
}
`;

if (typeof document !== "undefined" && !document.getElementById("adv-styles")) {
  const tag = document.createElement("style");
  tag.id = "adv-styles";
  tag.textContent = ADVERT_CSS;
  document.head.appendChild(tag);
}

/* ─── Constants ──────────────────────────────────────────────────────────── */
const P  = "rgb(160,0,48)";
const PL = "rgba(160,0,48,0.07)";
const PD = "rgb(110,0,32)";

const PLATFORMS = [
  {
    id: "linkedin", label: "LinkedIn", maxLen: 3000, color: "#0A66C2",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
      </svg>
    ),
  },
  {
    id: "twitter", label: "Twitter / X", maxLen: 280, color: "#000",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.253 5.622 5.91-5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
      </svg>
    ),
  },
];

const TONES = [
  { id: "professional", label: "Professional" },
  { id: "casual",       label: "Casual"        },
  { id: "intelligent",  label: "Expert"         },
  { id: "emotional",    label: "Inspiring"      },
  { id: "humble",       label: "Humble"         },
  { id: "celebratory",  label: "Celebratory"    },
  { id: "funny",        label: "Funny"          },
];

const TONE_INSTRUCTIONS = {
  professional: "Polished, professional tone.",
  casual:       "Friendly and conversational tone.",
  funny:        "Light humor but still professional.",
  intelligent:  "Insightful and expert tone.",
  emotional:    "Warm, inspiring tone.",
  celebratory:  "Proud announcement tone.",
  humble:       "Modest and grateful tone.",
};

const buildPrompt = ({ service, tone, maxLength, platform }) =>
  `Write a ${platform} post in first person.
Tone: ${TONE_INSTRUCTIONS[tone]}
Service Name: ${service.title}
Description: ${service.description}
Duration: ${service.activeMinutes?.value} minutes
Price: ₹${service.prize}
Rules:
- Max ${maxLength} characters
- No emojis unless casual/funny
- No bullet points
- End with soft CTA
- Output only the post text`.trim();

/* ─── Main component ─────────────────────────────────────────────────────── */
const AdvertiseServicePage = ({ open, service, onClose, slug }) => {
  const authId = useSelector(s => s.loginDetailsApi?.chatDetails?.uId);

  const [platform,          setPlatform]          = useState("linkedin");
  const [tone,              setTone]              = useState("professional");
  const [postText,          setPostText]          = useState("");
  const [loading,           setLoading]           = useState(false);
  const [charCount,         setCharCount]         = useState(0);
  const [linkedInConnected, setLinkedInConnected] = useState(false);
  const [copied,            setCopied]            = useState(false);
  const [posting,           setPosting]           = useState(false);
  const [hasInteracted,     setHasInteracted]     = useState(false);

  const activePlatform = PLATFORMS.find(p => p.id === platform);
  const maxLen         = activePlatform?.maxLen || 3000;
  const overLimit      = charCount > maxLen;
  const isConnected    = platform === "linkedin" ? linkedInConnected : true;

  useEffect(() => {
    if (!authId) return;
    MyYuukke.checkLinkedInConnection()
      .then(res => setLinkedInConnected(res?.data?.connected === true))
      .catch(() => setLinkedInConnected(false));
  }, [authId]);

  useEffect(() => {
    if (!service) return;
    const def = `I'm excited to offer ${service.title}.\nA focused ${service.activeMinutes?.value}-minute session designed to deliver real value.\n\nInterested? Let's connect.`;
    setPostText(def);
    setCharCount(def.length);
    setHasInteracted(false);
  }, [service]);

  const generatePost = useCallback(async () => {
    if (!service) return;
    setLoading(true);
    try {
      const prompt = buildPrompt({ service, tone, maxLength: maxLen, platform });
      const res = await fetch(OPENROUTER_CHAT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt }),
      });
      if (!res.ok) throw new Error("AI generation failed");
      const json = await res.json();
      const text = (json?.data?.reply || "").trim();
      setPostText(text);
      setCharCount(text.length);
    } catch {
      toast.error("AI generation failed. Try again.");
    } finally {
      setLoading(false);
    }
  }, [service, tone, maxLen, platform]);

  const handleRegenerate = () => { setHasInteracted(true); generatePost(); };

  const handleShare = async () => {
    if (!postText || overLimit || posting) return;
    setPosting(true);
    try {
      const fullText = `${postText}\n\n${slug}`;
      if (platform === "linkedin") {
        await MyYuukke.postToLinkedIn({
          text: fullText, imageUrl: service.imageUrl || "",
          serviceId: String(service.id || ""), tone, url: slug,
        });
        toast.success("Posted to LinkedIn 🚀");
        onClose?.();
      } else if (platform === "twitter") {
        const tweetText = fullText.length <= 280 ? fullText : postText;
        const popup = window.open(
          "https://twitter.com/intent/tweet?text=" + encodeURIComponent(tweetText),
          "twitter-share", "width=600,height=450,scrollbars=yes,resizable=yes"
        );
        const pollTimer = setInterval(() => {
          try {
            if (!popup || popup.closed) { clearInterval(pollTimer); setPosting(false); onClose?.(); }
          } catch { clearInterval(pollTimer); setPosting(false); onClose?.(); }
        }, 500);
        toast.success("Twitter compose window opened! 🚀");
        return;
      }
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.message || `Failed to post`);
    } finally {
      setPosting(false);
    }
  };

  const handleConnect = () => {
    if (!authId) { toast.error("Session expired. Please refresh."); return; }
    const encodedId = encodeURIComponent(btoa(String(authId)));
    if (platform === "linkedin") window.location.href = `${APPCONFIG.API_URL}auth/linkedin?state=${encodedId}`;
    else if (platform === "twitter") window.location.href = `${APPCONFIG.API_URL}auth/twitter?state=${encodedId}`;
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(postText).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    });
  };

  const handlePlatformChange = (id) => { setPlatform(id); if (hasInteracted) generatePost(); };
  const handleToneChange     = (id) => { setTone(id);     if (hasInteracted) generatePost(); };

  if (!service) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth className="adv-dialog"
      PaperProps={{ style: { fontFamily: "'DM Sans', sans-serif" } }}>

      {/* ── Header ── */}
      <div style={{ background: P, padding: "16px 22px", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 10, color: "rgba(255,255,255,0.6)", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 3 }}>
            Share your service
          </div>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#fff", letterSpacing: "-0.3px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {service.title}
          </div>
        </div>
        <button onClick={onClose}
          style={{ background: "rgba(255,255,255,0.15)", border: "none", borderRadius: 8, color: "#fff", width: 32, height: 32, cursor: "pointer", fontSize: 18, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginLeft: 12 }}>
          ×
        </button>
      </div>

      <DialogContent style={{ padding: 0, overflow: "hidden" }}>
        <div className="adv-grid">

          {/* ── LEFT: Compose ── */}
          <div className="adv-left">

            {/* Platform */}
            <Label>Platform</Label>
            <div className="adv-platform-row" style={{ marginBottom: 18 }}>
              {PLATFORMS.map(pl => (
                <button key={pl.id} className="adv-platform-btn"
                  onClick={() => handlePlatformChange(pl.id)}
                  style={{
                    border: `2px solid ${platform === pl.id ? pl.color : "rgba(0,0,0,0.1)"}`,
                    background: platform === pl.id ? `${pl.color}12` : "#fff",
                    color: platform === pl.id ? pl.color : "#888",
                  }}>
                  <span style={{ color: platform === pl.id ? pl.color : "#aaa" }}>{pl.icon}</span>
                  {pl.label}
                </button>
              ))}
            </div>

            {/* Tone */}
            <Label>Tone</Label>
            <div className="adv-tone-row" style={{ marginBottom: 18 }}>
              {TONES.map(t => (
                <button key={t.id}
                  onClick={() => handleToneChange(t.id)}
                  style={{
                    padding: "6px 12px", borderRadius: 99, fontSize: 12, cursor: "pointer",
                    border: `1.5px solid ${tone === t.id ? P : "rgba(0,0,0,0.12)"}`,
                    background: tone === t.id ? PL : "#fff",
                    color: tone === t.id ? PD : "#555",
                    fontWeight: tone === t.id ? 600 : 400,
                    fontFamily: "inherit", transition: "all 0.15s",
                  }}>
                  {t.label}
                </button>
              ))}
            </div>

            {/* Textarea */}
            <Label>Your post</Label>
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 6 }}>
              <span style={{ fontSize: 12, color: overLimit ? "#e63946" : "#aaa", fontWeight: overLimit ? 700 : 400 }}>
                {charCount} / {maxLen}
              </span>
            </div>
            <div style={{ position: "relative", marginBottom: 14 }}>
              {loading && <Spinner />}
              <textarea rows={7} className="adv-textarea"
                value={postText} disabled={loading}
                onChange={e => { setPostText(e.target.value); setCharCount(e.target.value.length); }}
                style={{ border: `1.5px solid ${overLimit ? "#e63946" : "rgba(0,0,0,0.12)"}` }}
              />
            </div>

            {/* Regenerate */}
            <button className="adv-btn" onClick={handleRegenerate} disabled={loading}
              style={{ background: PL, color: PD, border: "1px solid rgba(160,0,48,0.2)", marginBottom: 10, opacity: loading ? 0.6 : 1 }}>
              <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" viewBox="0 0 24 24">
                <path d="M4 12a8 8 0 0114-5.3M20 12a8 8 0 01-14 5.3" />
                <path d="M18 6.7V3M18 6.7H14.3M6 17.3V21M6 17.3H9.7" />
              </svg>
              {loading ? "Generating…" : "Regenerate with AI"}
            </button>

            {/* Share / Connect */}
            {!isConnected ? (
              <button className="adv-btn" onClick={handleConnect}
                style={{ background: activePlatform.color, color: "#fff" }}>
                {activePlatform.icon} Connect {activePlatform.label}
              </button>
            ) : (
              <button className="adv-btn" onClick={handleShare}
                disabled={overLimit || !postText || posting}
                style={{
                  background: overLimit ? "#ccc" : P, color: "#fff",
                  cursor: overLimit || posting ? "not-allowed" : "pointer",
                  opacity: posting ? 0.7 : 1,
                }}>
                {posting
                  ? <><SmallSpinner />Posting…</>
                  : <>
                      <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                        <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
                      </svg>
                      {platform === "twitter" ? "Open Twitter Compose" : `Post to ${activePlatform.label}`}
                    </>
                }
              </button>
            )}
          </div>

          {/* ── RIGHT: Preview ── */}
          <div className="adv-right">
            <Label>Live preview</Label>

            <div className="adv-preview-card">
              {/* Post header */}
              <div style={{ padding: "14px 16px 10px", display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 38, height: 38, borderRadius: "50%", background: P, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 15, fontWeight: 700, flexShrink: 0 }}>
                  {service.title?.[0]?.toUpperCase() || "Y"}
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#1a1a18" }}>You</div>
                  <div style={{ fontSize: 11, color: "#aaa", display: "flex", alignItems: "center", gap: 4 }}>
                    <span style={{ color: activePlatform.color }}>{activePlatform.icon}</span>
                    Sharing on {activePlatform.label}
                  </div>
                </div>
              </div>

              {/* Post body */}
              <div style={{ padding: "0 16px 14px", fontSize: 13, color: "#333", lineHeight: 1.7, whiteSpace: "pre-wrap", minHeight: 60 }}>
                {loading
                  ? <span style={{ color: "#bbb", fontStyle: "italic" }}>Generating your post…</span>
                  : postText || <span style={{ color: "#bbb", fontStyle: "italic" }}>Your post will appear here</span>
                }
              </div>

              {/* Service image card */}
              {service.imageUrl && (
                <div style={{ margin: "0 16px 14px", borderRadius: 10, overflow: "hidden", border: "1px solid rgba(0,0,0,0.08)" }}>
                  <img src={service.imageUrl} alt={service.title}
                    style={{ width: "100%", height: 110, objectFit: "cover", display: "block" }} />
                  <div style={{ padding: "9px 12px", background: "#fafafa" }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#1a1a18" }}>{service.title}</div>
                    <div style={{ fontSize: 11, color: "#aaa", marginTop: 2 }}>₹{service.prize} · {service.activeMinutes?.value} min</div>
                  </div>
                </div>
              )}

              {/* Char progress */}
              <div style={{ padding: "0 16px 14px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#bbb", marginBottom: 4 }}>
                  <span>Character count</span>
                  <span style={{ color: overLimit ? "#e63946" : charCount > maxLen * 0.8 ? "#c97b2a" : "#1a9e5e", fontWeight: 600 }}>
                    {charCount} / {maxLen}
                  </span>
                </div>
                <div style={{ background: "#f0f0ee", borderRadius: 99, height: 4, overflow: "hidden" }}>
                  <div style={{
                    height: "100%", borderRadius: 99,
                    width: `${Math.min((charCount / maxLen) * 100, 100)}%`,
                    background: overLimit ? "#e63946" : charCount > maxLen * 0.8 ? "#c97b2a" : "#1a9e5e",
                    transition: "width 0.3s, background 0.3s",
                  }} />
                </div>
              </div>
            </div>

            {/* Copy */}
            <button className="adv-btn" onClick={handleCopy}
              style={{ background: "#fff", color: "#555", border: "1px solid rgba(0,0,0,0.12)", marginTop: 12 }}>
              {copied
                ? <><CheckIcon color="#1a9e5e" /><span style={{ color: "#1a9e5e" }}>Copied!</span></>
                : <><CopyIcon />Copy text</>
              }
            </button>

            {/* Notes */}
            {platform === "twitter" && charCount > 240 && !overLimit && (
              <Note color="#c97b2a" bg="rgba(201,123,42,0.07)" border="rgba(201,123,42,0.2)">
                Getting close to the 280 character limit.
              </Note>
            )}
            {platform === "twitter" && (
              <Note color="#666" bg="rgba(0,0,0,0.03)" border="rgba(0,0,0,0.08)">
                💡 Twitter will automatically preview your service image when the link is shared.
              </Note>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* ─── Sub-components ─────────────────────────────────────────────────────── */
const Label = ({ children }) => (
  <div style={{ fontSize: 11, fontWeight: 600, color: "#888", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 8 }}>
    {children}
  </div>
);

const Note = ({ children, color, bg, border }) => (
  <div style={{ marginTop: 10, background: bg, border: `1px solid ${border}`, borderRadius: 10, padding: "10px 14px", fontSize: 12, color, lineHeight: 1.5 }}>
    {children}
  </div>
);

const Spinner = () => (
  <div style={{ position: "absolute", inset: 0, background: "rgba(255,255,255,0.8)", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2 }}>
    <div style={{ width: 28, height: 28, borderRadius: "50%", border: "3px solid rgba(160,0,48,0.15)", borderTop: "3px solid rgb(160,0,48)", animation: "spin 0.8s linear infinite" }} />
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
  </div>
);

const SmallSpinner = () => (
  <div style={{ width: 14, height: 14, borderRadius: "50%", border: "2px solid rgba(255,255,255,0.3)", borderTop: "2px solid #fff", animation: "spin 0.8s linear infinite" }} />
);

const CopyIcon = () => (
  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <rect x="9" y="9" width="13" height="13" rx="2" />
    <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
  </svg>
);

const CheckIcon = ({ color }) => (
  <svg width="14" height="14" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

export default AdvertiseServicePage;